import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  useTheme,
} from '@mui/material';

interface SuccessDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message: string;
  buttonText?: string;
  autoCloseDelay?: number; // in milliseconds, 0 to disable auto-close
}

const SuccessDialog: React.FC<SuccessDialogProps> = ({
  open,
  onClose,
  title,
  message,
  buttonText = 'Got it',
  autoCloseDelay = 5000, // Default 5 seconds
}) => {
  const theme = useTheme();
  
  // Auto-dismiss dialog after specified delay
  useEffect(() => {
    if (open && autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      
      return () => clearTimeout(timer);
    }
  }, [open, autoCloseDelay, onClose]);

  // Determine if we're in dark mode based on theme
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth={false}
      sx={{
        zIndex: 9999, // Ensure it appears above everything
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          backgroundColor: isDarkMode ? '#081028' : '#ffffff', // Force the correct color with          border: isDarkMode ? '1px solid #ffffff' : '1px solid #e2e8f0',
          position: 'relative',
          zIndex: 10000,
          width: '400px', // Fixed square-ish width
          maxWidth: '400px',
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dark overlay
          zIndex: 9998,
        }
      }}
    >
      <DialogContent sx={{ p: 4, textAlign: 'center' }}>
        {/* Header */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: isDarkMode ? '#ffffff' : '#202224',
            mb: 2,
            fontSize: '1.5rem',
            fontFamily: '"Nunito Sans", sans-serif',
            letterSpacing: '-0.11px',
          }}
        >
          {title}
        </Typography>
        
        {/* Body */}
        <Typography
          variant="body1"
          sx={{
            color: isDarkMode ? '#ffffff' : '#666',
            mb: 4,
            fontSize: '1rem',
            lineHeight: 1.5,
            fontFamily: '"Nunito Sans", sans-serif',
            letterSpacing: '-0.06px',
            opacity: 0.8,
          }}
        >
          {message}
        </Typography>
        
        {/* Action Button */}
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            width: '100%',
            py: 1.5,
            borderRadius: 2,
            backgroundColor: '#1B4672',
            color: '#ffffff',
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: '"Nunito Sans", sans-serif',
            '&:hover': {
              backgroundColor: '#1B4672',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.3s ease-in-out',
          }}
        >
          {buttonText}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default SuccessDialog;
